import { useApolloClient, useQuery } from '@apollo/client/react';
import { useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { JobList } from '@/components/JobList/JobList';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { Spinner } from '@/components/Spinner/Spinner';
import { allJobs, isInFlight, JOBS } from '@/papermill/api';
import { prefetchJob } from '@/papermill/extraction-cache';

import { useDeleteJob } from './useDeleteJob';

const POLL_MS = 3000;

/** Every playground job, documents and recordings alike, live while any is still running. */
export function PlaygroundContainer() {
  const navigate = useNavigate();
  const client = useApolloClient();
  const location = useLocation();
  const highlightId = (location.state as { highlight?: string } | null)?.highlight ?? null;
  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery(JOBS, {
    fetchPolicy: 'cache-and-network',
  });
  const jobs = useMemo(() => allJobs(data), [data]);
  const deletion = useDeleteJob();
  const running = jobs.some(isInFlight);

  useEffect(() => {
    if (running) {
      startPolling(POLL_MS);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [running, startPolling, stopPolling]);

  const newJob = (
    <Button icon="+" onClick={() => navigate('/playground/new')}>
      New job
    </Button>
  );

  return (
    <>
      <PageHeader
        title="Playground"
        lede="Turn a document or a recording into a graph of the people, companies, accounts and addresses in it — with every value traced back to the page, or the moment, it came from."
        actions={jobs.length > 0 ? newJob : null}
      />
      {loading && !data ? <Spinner /> : null}
      {error && !data ? (
        <Callout tone="danger" title="Could not load your jobs">
          <Button variant="ghost" onClick={() => void refetch()}>
            Try again
          </Button>
        </Callout>
      ) : null}
      {data ? (
        <JobList
          jobs={jobs}
          highlightId={highlightId}
          renderLink={(job, children) => (
            <Link
              to={`/playground/${job.id}`}
              onPointerEnter={
                job.status === 'COMPLETE' ? () => void prefetchJob(client, job.id) : undefined
              }
              onFocus={
                job.status === 'COMPLETE' ? () => void prefetchJob(client, job.id) : undefined
              }
            >
              {children}
            </Link>
          )}
          emptyAction={newJob}
          onDelete={deletion.requestDelete}
        />
      ) : null}
      {deletion.dialog}
    </>
  );
}
